<template>
  <nav class="navbar-container" :class="{ scrolled: isScrolled }">
    <!-- Desktop Navbar -->
    <div class="navbar-desktop">
      <!-- Logo Section -->
      <div class="logo-section">
        <router-link to="/">
          <div class="logo">
            <span class="logo-icon"><img :src="settings.logo || fallbackLogo" alt="Logo"></span>
          </div>
        </router-link>
      </div>

      <!-- Navbar border -->
      <div class="navbar-border"></div>
    </div>

    <!-- Mobile Menu Button -->
    <button 
      class="mobile-menu-btn"
      :class="{ active: menuOpen }"
      @click="toggleMenu"
      aria-label="Toggle menu"
    >
      <span class="hamburger-line line1"></span>
      <span class="hamburger-line line2"></span>
      <span class="hamburger-line line3"></span>
    </button>

    <!-- Fullscreen Mega Menu -->
    <transition name="menu-slide">
      <div v-if="menuOpen" class="mobile-menu">
        <div class="mobile-menu-content">
          <!-- Menu Grid -->
          <div class="menu-grid">
            <div 
              v-for="(category, index) in menuCategories" 
              :key="index" 
              class="menu-column"
              :style="{ '--index': index }"
            >
              <h3 class="column-title">{{ category.title }}</h3>
              <ul class="column-links">
                <li v-for="(item, i) in category.items" :key="i">
                  <!-- External Link -->
                  <a 
                    v-if="item.external && !item.popup"
                    :href="item.route" 
                    target="_blank" 
                    rel="noopener"
                    class="menu-link"
                    @click="closeMenu"
                  >
                    {{ item.label }}
                  </a>
                  
                  <!-- Popup Link -->
                  <a 
                    v-else-if="item.popup"
                    href="javascript:void(0)"
                    class="menu-link"
                    @click="openPopup(item.route)"
                  >
                    {{ item.label }}
                  </a>
                  
                  <!-- Internal Link -->
                  <router-link 
                    v-else
                    :to="item.route"
                    class="menu-link"
                    @click="closeMenu"
                  >
                    {{ item.label }}
                  </router-link>
                </li>
              </ul>
            </div>
          </div>

          <!-- Top Right Links -->
         
          <div class="top-left-links">
            <div class="logo-section">
        <router-link to="/">
          <div class="logo">
            <span class="logo-icon"><img :src="settings.logo || fallbackLogo" alt="Logo"></span>
          </div>
        </router-link>
      </div>
          </div>

          <!-- Decorative Elements -->
          <div class="menu-decoration decoration-1"></div>
          <div class="menu-decoration decoration-2"></div>
        </div>
      </div>
    </transition>

    <!-- Menu Overlay -->
    <transition name="overlay-fade">
      <div 
        v-if="menuOpen" 
        class="menu-overlay"
        @click="closeMenu"
      ></div>
    </transition>

    <!-- Tour Popup -->
    <transition name="popup-fade">
      <div v-if="showPopup" class="popup-overlay" @click="closePopup">
        <div class="popup-container" @click.stop>
          <!-- Close Button -->
          <button class="popup-close" @click="closePopup">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          
          <!-- Popup Header -->
          <div class="popup-header">
            <!-- <h2>Virtual School Tour</h2>
            <p>Explore our school in 360°</p> -->
          </div>

          <!-- Iframe Content -->
          <div class="popup-content">
            <iframe 
              :src="popupUrl" 
              frameborder="0" 
              allowfullscreen
              class="popup-iframe"
            ></iframe>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>

import api from '@/services/api';
import { siteSettings, loadSettings } from '@/services/settings';

export default {
  name: 'Navbar',
  data() {
    return {
      menuOpen: false,
      isScrolled: false,
      showPopup: false,
      popupUrl: '',
      settings: siteSettings,
      fallbackLogo: require('../assets/logo2.png'),
      menuCategories: [
        {
          title: 'ABOUT US',
          items: [
            { label: '#Home', route: '/' },
            { label: 'Vision, Mission & Values', route: '/vision' },
            { label: 'Governance', route: '/governance' },
            { label: 'Staff', route: '/staff' },
            { label: 'Facilities', route: '/facilities' },
            { label: 'Accreditations & Affiliations', route: '/accreditations' },
            { label: '#Contact Us', route: '/Contact' }
          ]
        },
        {
          title: 'ADMISSIONS',
          items: [
            { label: '#Admission', route: '/sasadmission' },
            { label: 'Admissions Process', route: '/admission-process' },
            { label: 'FAQs', route: '/faqs' },
            { label: 'How to Apply', route: '/apply' },
            { label: 'Fees', route: '/fees' },
            { label: 'Uniform', route: '/uniform' },
            { label: '#Tour & Open Houses', route: 'https://tour.panoee.net/698caa80df1d8ef10da4b588/698e0234ed5840a8214030c5', popup: true }
          ]
        },
        {
          title: 'LEARNING',
          items: [
            { label: 'Learning Overview', route: '/learning' },
            { label: 'Early Years Program', route: '/early-years' },
            { label: 'Elementary School', route: '/elementary' },
            { label: 'Middle School', route: '/middle-school' },
            { label: 'High School', route: '/high-school' },
            { label: 'Student Support', route: '/support' }
          ]
        },
        {
          title: 'PARENTS',
          items: [
            { label: '#Parent Portal', route: 'https://www.orison.school/', external: true },
            { label: '#School Portal', route: 'http://alsholaho.fortiddns.com:8100/OrisonSchoolPortal/Login.aspx', external: true },
            { label: 'School Lunch', route: '/lunch' },
            { label: 'Handbooks & Policies', route: '/policies' },
            { label: 'Academic Calendar', route: '/calendar' }
          ]
        },
        {
          title: 'STUDENT LIFE',
          items: [
            { label: '#Gallery', route: '/Gallery' },
            { label: '#Testimonials', route: '/Testimonials' },
            { label: '#Policy', route: '/PrivacyPolicy' },
            { label: 'House Teams', route: '/houses' },
            { label: 'Wellbeing', route: '/wellbeing' }
          ]
        }
      ]
    }
  },
  async created() {
    await loadSettings();
    try {
      // Try aggregated /api/menu/ first, fallback to /api/menu-categories/
      let data;
      try {
        data = await api.getMenu();
      } catch (_) {
        data = await api.getMenuCategories();
      }
      const list = api.extractList(data);
      if (list.length > 0) {
        this.menuCategories = list.map(cat => ({
          title: cat.title,
          items: (cat.items || []).map(item => ({
            label: item.label || item.title,
            route: item.route || item.url || '/',
            external: item.is_external || item.link_type === 'external' || false,
            popup: item.is_popup || item.link_type === 'popup' || false
          }))
        }));
      }
    } catch (e) {
      console.warn('[Navbar] Menu API failed, using fallback:', e.message);
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('keydown', this.handleEscKey);
  },
  methods: {
    handleEscKey(event) {
      if (event.key === 'Escape' && this.showPopup) { this.closePopup(); }
      if (event.key === 'Escape' && this.menuOpen) { this.closeMenu(); }
    },
    openPopup(url) {
      this.popupUrl = url;
      this.showPopup = true;
      this.closeMenu();
      document.body.style.overflow = 'hidden';
    },
    closePopup() {
      this.showPopup = false;
      this.popupUrl = '';
      document.body.style.overflow = 'auto';
    },
    handleScroll() { this.isScrolled = window.scrollY > 100; },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      document.body.style.overflow = this.menuOpen ? 'hidden' : 'auto';
    },
    closeMenu() {
      this.menuOpen = false;
      document.body.style.overflow = 'auto';
    }
  },
  beforeUnmount() {
    document.body.style.overflow = 'auto';
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('keydown', this.handleEscKey);
  }
}

</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* ==================== Navbar Container ==================== */
.navbar-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: transparent;
  backdrop-filter: none;
  box-shadow: none;
  animation: slideDownIn 0.8s ease-out;
  transition: all 0.4s ease;
  min-height: 110px;
}

.navbar-container.scrolled {
  background: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  min-height: 40px;
}

@keyframes slideDownIn {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==================== Desktop Navbar ==================== */
.navbar-desktop {
  max-width: 1200px;
  margin: 0 auto;
  padding: 10px 40px 15px 40px;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-height: 60px;
  transition: all 0.4s ease;
  height: 100%;
}

.navbar-container.scrolled .navbar-desktop {
  min-height: 20px;
  padding: 2px 5px;
}

.navbar-container.scrolled .logo-icon {
  width: 16px;
  height: 16px;
}

.navbar-container.scrolled .mobile-menu-btn {
  top: 2px;
}

.navbar-container.scrolled .hamburger-line {
  background: #2c3e50;
}

/* ==================== Logo Section ==================== */
.logo-section {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: auto;
  padding: 5px 0 0 0;
  animation: zoomIn 0.8s ease-out 0.2s backwards;
  z-index: 1;
  position: relative;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  min-width: 40px;
  min-height: 40px;
  transition: all 0.4s ease;
}

.logo-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ==================== Mobile Menu Button ==================== */
.mobile-menu-btn {
  display: flex;
  flex-direction: column;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  z-index: 2000;
  position: fixed;
  top: 20px;
  right: 10%;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.hamburger-line {
  width: 30px;
  height: 3px;
  background: white;
  border-radius: 3px;
  margin: 4px 0;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.mobile-menu-btn.active .line1 {
  transform: rotate(45deg) translate(10px, 10px);
  background: white;
}

.mobile-menu-btn.active .line2 {
  opacity: 0;
  transform: translateX(-10px);
}

.mobile-menu-btn.active .line3 {
  transform: rotate(-45deg) translate(7px, -7px);
  background: white;
}

/* ==================== Mega Menu ==================== */
.mobile-menu {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #c02131c9 0%, #f7f02bb9 100%);;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;

}

.mobile-menu-content {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 120px 60px 60px 60px;
  position: relative;
}

/* ==================== Top left Links ==================== */
.top-left-links {
  position: absolute;
  top: 5px;
  left: 100px;
  display: flex;
  gap: 40px;
  z-index: 10;
  animation: fadeInDown 0.6s ease-out 0.3s backwards;
}

.top-link {
  color: white;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  position: relative;
}

.top-link::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: white;
  transition: width 0.3s ease;
}

.top-link:hover::after {
  width: 100%;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==================== Menu Grid ==================== */
.menu-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 60px;
  margin-top: 40px;
}

.menu-column {
  animation: slideInUp 0.6s ease-out backwards;
  animation-delay: calc(0.1s * var(--index));
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.column-title {
  color: white;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
}

.column-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.column-links li {
  margin-bottom: 15px;
}

.menu-link {
  color: white;
  text-decoration: none;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.6;
  transition: all 0.3s ease;
  display: inline-block;
  position: relative;
}

.menu-link::before {
  content: '';
  position: absolute;
  left: -15px;
  top: 50%;
  transform: translateY(-50%) scaleX(0);
  width: 8px;
  height: 2px;
  background: white;
  transition: transform 0.3s ease;
}

.menu-link:hover {
  transform: translateX(10px);
  opacity: 1;
}

.menu-link:hover::before {
  transform: translateY(-50%) scaleX(1);
}

/* ==================== Decorative Elements ==================== */
.menu-decoration {
  position: absolute;
  border-radius: 50%;
  opacity: 0.05;
  pointer-events: none;
  animation: float 8s ease-in-out infinite;
}

.decoration-1 {
  width: 400px;
  height: 400px;
  background: white;
  top: 5%;
  right: -10%;
  animation-delay: 0s;
}

.decoration-2 {
  width: 300px;
  height: 300px;
  background: white;
  bottom: 10%;
  left: -8%;
  animation-delay: 1.5s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  50% {
    transform: translateY(-30px) translateX(20px);
  }
}

/* ==================== Menu Overlay ==================== */
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 997;
}

/* ==================== Popup Styles ==================== */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.popup-container {
  background: white;
  border-radius: 20px;
  width: 95%;
  max-width: 1400px;
  height: 90vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  position: relative;
  animation: popupSlideIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(50px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.popup-close:hover {
  background: #C02130;
  transform: rotate(90deg) scale(1.1);
  box-shadow: 0 6px 20px rgba(192, 33, 48, 0.4);
}

.popup-close:hover svg {
  stroke: white;
}

.popup-close svg {
  stroke: #2c3e50;
  transition: stroke 0.3s ease;
}

.popup-header {
  background: linear-gradient(135deg, #C02130 0%, #F7F02B 100%);
  padding: 30px;
  text-align: center;
  color: white;
}

.popup-header h2 {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 10px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.popup-header p {
  font-size: 16px;
  margin: 0;
  opacity: 0.95;
}

.popup-content {
  flex: 1;
  position: relative;
  background: #f5f5f5;
}

.popup-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

/* ==================== Transitions ==================== */
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.menu-slide-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.menu-slide-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.4s ease;
}

.popup-fade-enter-active .popup-container,
.popup-fade-leave-active .popup-container {
  transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

.popup-fade-enter-from .popup-container {
  transform: scale(0.8) translateY(50px);
}

.popup-fade-leave-to .popup-container {
  transform: scale(0.9) translateY(30px);
  opacity: 0;
}

/* ==================== Responsive Design ==================== */
@media (max-width: 1200px) {
  .menu-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
    padding-top:400px
  }
  
  .top-right-links {
    right: 60px;
    gap: 30px;
  }
}

@media (max-width: 768px) {
  .navbar-container {
    min-height: 100px;
  }
  
  .navbar-desktop {
    padding: 0 20px 15px 20px;
    min-height: 100px;
  }

  .navbar-container.scrolled .navbar-desktop {
    min-height: 70px;
  }

  .logo-icon {
    width: 90px;
    height: 90px;
  }
  
  .navbar-container.scrolled .logo-icon {
    width: 60px;
    height: 60px;
  }

  .mobile-menu-btn {
    top: 10px;
    left: 80%;
  }

  .navbar-container.scrolled .mobile-menu-btn {
    top: 12px;
  }

  .menu-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 800px;
  }
  
  .mobile-menu-content {
    padding: 100px 30px 40px 30px;
  }
  
  .top-right-links {
    position: static;
    justify-content: center;
    margin-bottom: 30px;
    gap: 20px;
  }
  
  .column-title {
    font-size: 14px;
  }
  
  .menu-link {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .navbar-container {
    min-height: 90px;
  }
  
  .navbar-desktop {
    padding: 0 15px 12px 15px;
    min-height: 90px;
  }

  .navbar-container.scrolled .navbar-desktop {
    min-height: 65px;
  }

  .logo-icon {
    width: 80px;
    height: 80px;
  }

  .navbar-container.scrolled .logo-icon {
    width: 55px;
    height: 55px;
  }

  .navbar-container.scrolled .mobile-menu-btn {
    top: 10px;
  }

  .hamburger-line {
    width: 25px;
    height: 2.5px;
    margin: 3px 0;
  }

  .menu-grid {
    grid-template-columns: 1fr;
    gap: 30px;
    padding-top: 800px;
  }


  
  .mobile-menu-content {
    padding: 80px 20px 30px 20px;
  }
  
  .top-right-links {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
  
  .top-link {
    font-size: 12px;
  }
  
  .column-title {
    font-size: 13px;
    margin-bottom: 15px;
  }
  
  .menu-link {
    font-size: 13px;
  }
}

@media (max-width: 360px) {
  .navbar-container {
    min-height: 85px;
  }
  
  .logo-icon {
    width: 70px;
    height: 70px;
  }
  .menu-grid {
    grid-template-columns: 1fr;
    gap: 30px;
    padding-top: 800px;
  }
  
  .navbar-container.scrolled .logo-icon {
    width: 50px;
    height: 50px;
  }

  .navbar-container.scrolled .mobile-menu-btn {
    top: 8px;
  }

  .hamburger-line {
    width: 22px;
  }
}

/* Ensure hamburger lines are visible */
.navbar-container.scrolled .hamburger-line {
  background: #2c3e50 !important;
}

.mobile-menu-btn.active .hamburger-line {
  background: white !important;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}
</style>